'use client';

// Global
import type { SearchResultsStoreState } from '@sitecore-search/react';
import { WidgetDataType, widget } from '@sitecore-search/react';
import { useState, useRef, useEffect } from 'react';
import type { ComponentParams, Field, LinkField } from '@sitecore-content-sdk/nextjs';
import { useSitecoreContext } from 'lib/hooks/useSitecoreContext';
import { tv } from 'tailwind-variants';
import {
  Context as CloudSearchContext,
  getWidgetData,
  SearchWidgetItem,
  ComparisonFilter,
  WidgetRequestData,
  LogicalFilter,
} from '@sitecore-cloudsdk/search/browser';

// Local
import { useScrollElementIntoView } from 'lib/hooks/useScrollElementIntoView';
import { useSearchInitialization } from 'lib/context/SearchInitializationContext';
import {
  mapResultsToTrackEntities,
  trackSearchItemClick,
  trackSearchWidgetView,
} from 'lib/search/analytics';
import { getTestProps } from 'lib/testing/utils';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import RichTextWrapper from 'helpers/SitecoreWrappers/RichTextWrapper/RichTextWrapper';
import { getCtaStyle } from 'lib/utils/cta-utils';
import { parseStyleParams } from 'lib/utils/style-param-utils';
import { XceleratePage } from 'src/baseTypes/Xcelerate.HztlFoundation.model';
import useIsEditing from 'lib/hooks/useIsEditing';
import { useSetIsDataAvailable } from 'lib/context/IsDataAvailableContext';
import { editingHydrationProps } from 'lib/hooks/useEditingHydrationProps';

type NewsModel = {
  id: string;
  type?: string;
  title?: string;
  name?: string;
  url?: string;
  description?: string;
  date?: string;
  news_type?: string;
  image?: string;
  short_description?: string;
  source_id?: string;
};

type SearchResultsProps = {
  defaultSortType?: SearchResultsStoreState['sortType'];
  defaultPage?: SearchResultsStoreState['page'];
  defaultItemsPerPage?: SearchResultsStoreState['itemsPerPage'];
  defaultKeyphrase?: SearchResultsStoreState['keyphrase'];
  title?: Field<string>;
  description?: Field<string>;
  link?: LinkField;
  rfkId?: string;
  params?: ComponentParams;
  uid?: string;
  id?: string;
};

// Helper function to format date to DD/MM/YYYY
const formatDate = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return dateString; // Return original if invalid date
    }
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  } catch {
    return dateString; // Return original if parsing fails
  }
};

export const ReportsAndInsightsWithSearchComponent = ({
  defaultItemsPerPage = 4,
  title,
  description,
  link,
  rfkId,
  params,
  uid,
  id,
}: SearchResultsProps) => {
  const { sitecoreContext } = useSitecoreContext();
  const currentPage = sitecoreContext?.route as XceleratePage & { itemId?: string };
  const articleCardsRef = useRef<HTMLDivElement>(null);

  const defaultSiteLocale =
    process.env.NEXT_PUBLIC_DEFAULT_LANGUAGE || process.env.DEFAULT_LANGUAGE || 'en-US';
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [news, setNews] = useState<NewsModel[]>([]);
  const isEditing = useIsEditing();
  const setIsDataAvailable = useSetIsDataAvailable();
  const { isSearchInitialized } = useSearchInitialization();

  useEffect(() => {
    if (!isSearchInitialized) {
      return;
    }

    const fetchNews = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const rfk = rfkId || 'rfkid_reports_insights';

        // Build locale context
        const locale = defaultSiteLocale;
        const [language, countryRaw] = locale.split('-');
        const country = (countryRaw || 'US').toLowerCase();
        const ctx = new CloudSearchContext({
          locale: {
            language: (language || 'en').toLowerCase(),
            country,
          },
        });

        const searchSourceIds = process.env.NEXT_PUBLIC_SEARCH_NEWS_SOURCE_ID;

        // Define the filter to match key_contact_id with the route item ID
        const contactIdFilter = new ComparisonFilter('key_contact_id', 'eq', currentPage?.itemId);

        const typeFilter = new ComparisonFilter('type', 'eq', 'Press Release');

        const widgetItem = new SearchWidgetItem(
          'newsarticle',
          rfk,
          {
            limit: defaultItemsPerPage || 4,
            content: {},
            filter: new LogicalFilter('and', [contactIdFilter, typeFilter]),
          },
          searchSourceIds ? [searchSourceIds] : undefined
        );
        const response = await getWidgetData(new WidgetRequestData([widgetItem]), ctx);
        const content = response?.widgets?.[0]?.content as NewsModel[] | undefined;
        const available = !isEditing && (content?.length ?? 0) > 0;
        setIsDataAvailable(available);
        setNews(content || []);

        if (content && content.length > 0) {
          void trackSearchWidgetView({
            widgetId: rfk,
            entities: mapResultsToTrackEntities(content, 'newsarticle'),
            request: {
              numResults: content.length,
              numRequested: defaultItemsPerPage || 4,
            },
          });
        }
      } catch {
        setError('Failed to load news');
      } finally {
        setIsLoading(false);
      }
    };

    // Only run client-side
    if (typeof window !== 'undefined') {
      fetchNews();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rfkId, defaultSiteLocale, defaultItemsPerPage, isSearchInitialized]);

  useScrollElementIntoView(articleCardsRef.current, {
    scrollTargetId: 'search-result-cards',
  });

  const { RenderingIdentifier } = params || {};

  const hasContent = Boolean(title?.value || description?.value);
  const hasCtas = Boolean(link?.value?.href);
  const styles = parseStyleParams(params as ComponentParams, ['cta1']);

  const {
    base,
    contentWrapper,
    titleText,
    descriptionText,
    divider,
    ctaContainer,
    ctaWrapper,
    newsList,
    newsListItem,
    newsListItemLink,
  } = TAILWIND_VARIANTS();

  if (!isEditing && !news?.length) {
    return <></>;
  }

  return (
    <section
      id={id}
      data-component="authorable/shared/search/ExpertCommentsOnNews"
      {...editingHydrationProps(isEditing)}
      {...getTestProps(`component-search-result-${uid}`)}
    >
      <div id={RenderingIdentifier} {...getTestProps(`search-result-cards`)}>
        <div className={base()}>
          <div className={contentWrapper({ hasContent })}>
            <PlainTextWrapper className={titleText()} field={title} tag="h2" />
            <RichTextWrapper className={descriptionText()} field={description} />
            {hasContent && <div className={divider()} />}
          </div>
          <div className={contentWrapper({ hasContent })}>
            {isLoading && (
              <ul className={newsList()}>
                <li className={newsListItem()}>
                  <span>Loading…</span>
                </li>
              </ul>
            )}
            {!isLoading && !error && news?.length > 0 && (
              <ul className={newsList()}>
                {news.map((n, idx) => (
                  <li key={n?.id || idx} className={newsListItem()}>
                    {n?.date && <span className="block">{formatDate(n?.date)}</span>}
                    <a
                      className={newsListItemLink()}
                      href={n?.url || '#'}
                      onClick={() => {
                        if (!n?.id) {
                          return;
                        }
                        void trackSearchItemClick({
                          widgetId: rfkId || 'rfkid_reports_insights',
                          entity: {
                            id: n.id,
                            entity: 'newsarticle',
                            uri: n.url,
                          },
                          itemPosition: idx,
                        });
                      }}
                    >
                      {n?.name || n?.title || 'Untitled'}
                    </a>
                  </li>
                ))}
              </ul>
            )}
            {!isLoading && error && (
              <ul className={newsList()}>
                <li className={newsListItem()}>
                  <span>{error}</span>
                </li>
              </ul>
            )}
            {hasCtas && (
              <div className={ctaContainer()}>
                <div className={ctaWrapper()}>
                  <LinkWrapper
                    ctaComponentClass="component-card-item-button-1-color"
                    ctaStyle={getCtaStyle(styles.cta1, 'primary')}
                    field={link}
                    ctaVariant="primary"
                    ctaIcon="arrow-right"
                    {...getTestProps(`primary-cta`)}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

const ReportsAndInsightsListSearchWidget = widget(
  ReportsAndInsightsWithSearchComponent,
  WidgetDataType.SEARCH_RESULTS,
  'newsarticle'
);

export default ReportsAndInsightsListSearchWidget;

const TAILWIND_VARIANTS = tv({
  slots: {
    base: [
      'flex',
      'bg-component-section-bg',
      'gap-component-section-container-gutter',
      'relative',
      'flex-col',
      'lg:flex-row',
      'py-component-section-container-padding-y',
    ],
    contentWrapper: [
      'contentWrapper',
      'flex',
      'flex-col',
      'w-full',
      'h-auto',
      'lg:w-1/2',
      'gap-space-between-base',
    ],
    titleText: [
      'text-component-section-title',
      'font-typography-font-family-header',
      'text-typography-font-size-xlarge',
      'leading-typography-line-height-xlarge',
    ],
    descriptionText: [
      'text-component-section-body',
      'font-typography-font-family-body',
      'text-typography-font-size-body-large',
      'leading-typography-line-height-body-large',
    ],
    divider: [
      'w-component-section-divider-width',
      'h-component-section-divider-height',
      'bg-component-section-divider',
    ],
    ctaContainer: ['flex', 'flex-col', 'w-full', 'md:justify-start'],
    ctaWrapper: [
      'flex',
      'gap-spacing-spacing-8',
      'flex-col',
      'md:flex-row',
      'md:gap-spacing-spacing-16',
    ],
    newsList: [],
    newsListItem: [
      'text-color-core-color-1-Navy',
      'font-typography-font-family-body',
      'text-typography-font-size-body-medium',
      'leading-typography-line-height-body-medium',
      '[&:not(:last-child)]:mb-[20px]',
    ],
    newsListItemLink: [
      'text-color-text-link',
      'underline',
      'hover:no-underline',
      'text-typography-font-size-body-large',
      'leading-typography-line-height-body-large',
    ],
  },
  variants: {
    hasContent: {
      true: {
        contentWrapper: 'gap-space-between-base',
      },
      false: {
        contentWrapper: 'gap-0',
      },
    },
  },
  defaultVariants: {
    hasContent: false,
  },
});
